// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { ChangeEvent, ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
    session: state.context.session,
    addingProductToCart: state.context.addingProductToCart,
});

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export interface OrderTermsStyles {
    textTitle?: TypographyProps;
    description?: TypographyProps;
    descriptionTextArea?: TextAreaProps;
    addToCartButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    productNoGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    errorMsgGridItem?: GridItemProps;
    editableQuantityTextField?: TextFieldPresentationProps;
    descriptionWrapper?: InjectableCss;
}

export const orderTermsStyles: OrderTermsStyles = {
    textTitle: { variant: "h6", transform: "none" },
    description: {
        forwardAs: "p",
        css: css`
            width: 100%;
        `,
    },
    container: {
        gap: 30,
    },
    productNoGridItem: {
        width: [8, 9, 10, 10, 10],
    },
    qtyGridItem: {
        width: [4, 3, 2, 2, 2],
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    buttonGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    errorMsgGridItem: { width: 12 },
    editableQuantityTextField: {
        cssOverrides: {
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                padding: 0 5px !important;
            `,
        },
    },
    descriptionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    addToCartButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = orderTermsStyles;

const NonStockItems = ({ addToCart, customSettings, session, addingProductToCart }: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [partNo, setPartNo] = React.useState("");
    const [qty, setQty] = useState("1");
    const [description, setdescription] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState<React.ReactNode>("");
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [partNoError, setPartNoError] = useState<ReactNode>("");
    const [descriptionError, setDescriptionError] = useState<ReactNode>("");
    const [quantityError, setQuantityError] = useState<ReactNode>("");

    if (session && !session.isAuthenticated) {
        return null;
    }

    const addProductToCart = async () => {
        if (!validateData()) {
            return;
        }

        setIsSubmitted(true);
        await addToCart({
            productId: customSettings.nonStockProductId,
            qtyOrdered: qty,
            properties: {
                isWidgetNonStockItem: "true",
                nonStockPartNo: partNo,
                nonStockDescription: description,
            },
            onError: (e: any) => {
                setIsSubmitted(false);
                setErrorMessage(e);
            },
        } as any);

        toasterContext.addToast({ body: translate("Item added to cart"), messageType: "success" });
        setQty("1");
        setdescription("");
        setPartNo("");
        setIsSubmitted(false);
    };

    const qtyChangeHandler = (event: any) => {
        const value = event.target.value;
        // <xng-change: check the value and eliminate invalid chars>
        let transformedValue = value;
        if (value) {
            transformedValue = value.replace(/[^0-9]/g, "");
            event.target.value = transformedValue;
        }
        // </xng-change>
        setQty(transformedValue);
        validateQty(transformedValue);
    };

    const partNoChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const partNo = event.currentTarget.value;
        setPartNo(partNo);
        validatePartNumber(partNo);
    };

    const validatePartNumber = (partNumber: string) => {
        const partNumberValid = partNumber.trim();
        setPartNoError(partNumberValid ? "" : siteMessage("McMc_Field_Required"));
        return partNumberValid;
    };

    const descriptionChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const description = event.currentTarget.value;
        setdescription(description);
        validateDescription(description);
    };

    const validateQty = (qty: string) => {
        const qtyValid = Number(qty) > 0 && qty.trim();
        setQuantityError(qtyValid ? "" : siteMessage("McMc_Field_Required"));
        return qtyValid;
    };

    const validateDescription = (description: string) => {
        const descriptionValid = description.trim();
        setDescriptionError(descriptionValid ? "" : siteMessage("McMc_Field_Required"));
        return descriptionValid;
    };

    const validateData = (): boolean => {
        const isPartNoValid = validatePartNumber(partNo);
        const isQtyValid = validateQty(qty);
        const isDescriptionValid = validateDescription(description);
        return !!(isPartNoValid && isQtyValid && isDescriptionValid);
    };

    return (
        <Accordion headingLevel={1}>
            <AccordionSection headerProps={{ "aria-level": 1 }} title={translate("Add Nonstock Item")}>
                <GridContainer {...styles.container} data-test-selector="productSelector">
                    <GridItem {...styles.descriptionGridItem}>
                        <Typography {...styles.textTitle}>{translate("Can't Find What You Need?")}</Typography>
                        <Typography {...styles.description}>{siteMessage("McMc_NonStockItem_description")}</Typography>
                    </GridItem>
                    <GridItem {...styles.productNoGridItem}>
                        <TextField
                            type="text"
                            label={translate("Part #")}
                            value={partNo}
                            onChange={partNoChangeHandler}
                            data-test-selector="nonStock_partNo"
                            required
                            error={partNoError}
                        />
                    </GridItem>
                    <GridItem {...styles.qtyGridItem}>
                        <TextField
                            {...styles.editableQuantityTextField}
                            type="text"
                            maxLength={6}
                            label={translate("QTY")}
                            value={qty}
                            onChange={qtyChangeHandler}
                            data-test-selector="nonStock_qty"
                            required
                            error={quantityError}
                        />
                    </GridItem>
                    <GridItem {...styles.descriptionGridItem}>
                        <TextArea
                            label={translate("Description")}
                            value={description}
                            onChange={descriptionChangeHandler}
                            data-test-selector="productDescription"
                            {...styles.descriptionTextArea}
                            required
                            error={descriptionError}
                        />
                    </GridItem>
                    {errorMessage && (
                        <GridItem {...styles.errorMsgGridItem}>
                            <Notify messageType="danger" hideCloseButton={true}>
                                {errorMessage}
                            </Notify>
                        </GridItem>
                    )}
                    <GridItem {...styles.buttonGridItem}>
                        <Button
                            {...styles.addToCartButton}
                            onClick={addProductToCart}
                            disabled={isSubmitted || addingProductToCart}
                            data-test-selector="nonStock_atc"
                        >
                            <EllipsisSpinner label={translate("Add to Cart")} showSpinner={isSubmitted} />
                        </Button>
                    </GridItem>
                </GridContainer>
            </AccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NonStockItems),
    definition: {
        group: "Common",
    },
};

export default widgetModule;
